import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './LocationPicker.css';

// ---------------------------------------------------------------------------
// The location control.
//
// Previously this was a full-width bar sitting above the listings, with a
// second "Use my current location" line under it — which put two search bars
// on the page and made a whole row tappable when only the words were meant to
// be. It is now a single pin-marked pill in the top right of the results
// header that opens a small panel, the pattern every delivery app uses: the
// chosen location is the thing worth showing all the time, and the machinery
// for changing it only needs to exist while it is being changed.
// ---------------------------------------------------------------------------

// Resolves any free-text location (city, postcode, address — any country's
// format) via the geocodeSearchQuery Cloud Function into { coords, label }.
// Small in-memory cache since the same query (e.g. a re-open of the panel, or
// a typo fix that lands back on the previous text) can fire more than once in
// a session.
const locationCache = new Map();
async function geocodeLocation(text) {
  const key = text.trim().toLowerCase();
  if (!key) return null;
  if (locationCache.has(key)) return locationCache.get(key);
  try {
    const geocodeSearchQuery = httpsCallable(functions, 'geocodeSearchQuery');
    const res = await geocodeSearchQuery({ query: text });
    const result = res.data?.found
      ? { coords: { lat: res.data.lat, lng: res.data.lng }, label: res.data.city || res.data.formattedAddress || text }
      : null;
    locationCache.set(key, result);
    return result;
  } catch (err) {
    // Being refused for asking too often is a different thing from "no such
    // place", and it has its own message worth showing — telling someone to
    // check their spelling when the real answer is "wait a moment" sends them
    // round in circles. Nothing else changes: an ordinary failure stays a soft
    // null, and neither outcome is cached.
    if (err?.code === 'functions/resource-exhausted') throw err;
    return null;
  }
}

export default function LocationPicker({ location, onChange }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on a click anywhere else or on Escape, and put focus back on the
  // pill afterwards so keyboard users are not dropped at the top of the page.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e) {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  async function handleSubmit(e) {
    e.preventDefault();
    const clean = input.trim();
    if (!clean) {
      setError('Please enter a ZIP code or address.');
      return;
    }
    setError('');
    setSearching(true);
    let result;
    try {
      result = await geocodeLocation(clean);
    } catch (err) {
      setSearching(false);
      setError(err.message || 'Too many location lookups just now. Please wait a moment and try again.');
      return;
    }
    setSearching(false);
    if (result) {
      onChange(result);
      setInput('');
      setOpen(false);
    } else {
      setError("We couldn't find that location. Please check and try again.");
    }
  }

  // Browser Geolocation API (it prompts for permission itself), reverse
  // geocoded server-side into the same {coords,label} shape the typed path
  // produces.
  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setError("Your browser doesn't support location detection.");
      return;
    }
    setError('');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const geocodeCoordinates = httpsCallable(functions, 'geocodeCoordinates');
          const res = await geocodeCoordinates({ lat: latitude, lng: longitude });
          if (res.data?.found) {
            onChange({
              coords: { lat: latitude, lng: longitude },
              label: res.data.city || res.data.formattedAddress,
            });
            setInput('');
            setOpen(false);
          } else {
            setError("Couldn't determine your location. Please enter it manually.");
          }
        } catch (err) {
          setError(
            err?.code === 'functions/resource-exhausted'
              ? err.message
              : "Couldn't determine your location. Please enter it manually."
          );
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        setError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied. Please enter your ZIP code or address instead.'
            : "Couldn't get your location. Please enter it manually."
        );
      },
      { timeout: 10000 }
    );
  }

  function handleClear() {
    onChange(null);
    setInput('');
    setError('');
    setOpen(false);
  }

  return (
    <div className="location-picker" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`location-picker__pill${location ? ' location-picker__pill--set' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        /* Spelled out because the visible label is hidden on the narrowest
           phones, which would otherwise leave this button with nothing but two
           decorative glyphs to announce itself by. */
        aria-label={location ? `Location: ${location.label}. Change location` : 'Set your location'}
      >
        <span className="location-picker__pin" aria-hidden="true">📍</span>
        <span className="location-picker__label">{location ? location.label : 'Set location'}</span>
        <span className="location-picker__caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="location-picker__panel" role="dialog" aria-label="Choose your location">
          {/* The full place name. The pill in the navbar shares its row with
              the logo and the menu button, so a long one is cut short there —
              this is where it is always readable in full. */}
          {location && (
            <p className="location-picker__current">
              Showing results near <strong>{location.label}</strong>
            </p>
          )}

          <form onSubmit={handleSubmit} className="location-picker__form">
            <input
              ref={inputRef}
              className="location-picker__input"
              type="text"
              /* Kept short on purpose: this has to stay readable in full on a
                 320px screen, which the old "Enter your address or ZIP code to
                 see what's nearby." never was. */
              placeholder="ZIP code or address"
              value={input}
              onChange={e => setInput(e.target.value)}
              aria-label="Your ZIP code or address"
            />
            <button type="submit" className="location-picker__go" disabled={searching}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </form>

          <button
            type="button"
            className="location-picker__geo"
            onClick={handleUseMyLocation}
            disabled={locating}
          >
            {locating ? (
              <span className="location-picker__spinner" aria-hidden="true" />
            ) : (
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.6" />
                <circle cx="10" cy="10" r="1.2" fill="currentColor" />
                <path d="M10 1.5v3M10 15.5v3M1.5 10h3M15.5 10h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            )}
            {locating ? 'Finding you…' : 'Use my current location'}
          </button>

          {location && (
            <button type="button" className="location-picker__reset" onClick={handleClear}>
              Show all locations
            </button>
          )}

          {error && <p className="location-picker__error">{error}</p>}
        </div>
      )}
    </div>
  );
}
